<template>
  <div ref="el" @click.stop="() => toggle()">
    <slot :open="show"></slot>

    <div v-if="show">
      <UiFloating
        ref="floating"
        :show="show && !disabled"
        :source="el"
        :placement="placement"
        class="bg-neutral-50 border border-neutral-400/50 dark:bg-neutral-800 dark:border-neutral-600 rounded-lg shadow-lg overflow-hidden"
        @closed="show = false"
        @click="() => closeOnClick && close()"
      >
        <slot name="content"></slot>
      </UiFloating>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { onClickOutside } from '@vueuse/core'
import type { Placement } from '@floating-ui/vue'

import UiFloating from './util/floating.vue'

const props = withDefaults(
  defineProps<{
    placement?: Placement
    disabled?: boolean
    closeOnClick?: boolean
  }>(),
  {
    placement: 'bottom-end',
    disabled: false
  }
)

const floating = ref<InstanceType<typeof UiFloating> | null>()
const el = ref<HTMLDivElement>()

const show = ref(false)

const open = () => {
  if (!show.value)
    show.value = true
}

const close = () => {
  if (show.value && floating.value)
    floating.value.close()
}

const toggle = () => {
  if (props.disabled) return
  if (show.value) close()
  else open()
}

onClickOutside(floating, (e: PointerEvent) => {
  if (!show.value) return
  else if ((e.target as HTMLElement)?.closest('.ui-dp-element') !== el.value)
    close()
})

defineExpose({
  isOpen: computed(() => show.value),
  open,
  close,
  toggle
})
</script>
